import { z } from 'zod';
import { withinJsonLength } from './common';
import { pageSlugSchema } from './pages';
import { interactiveTemplateNameSchema } from './interactive-template';

/**
 * Interactive messages (wayai-pro/wayai#4751): a message that carries a component in
 * `metadata.interactive`, beside `message_text`, which is always the readable fallback. The row's
 * `message_type` is `'interactive'` (the ConversationDO `message` table already admits it). That type
 * is NOT exclusive to components: an inbound WhatsApp button or list reply is stored with
 * `message_type: 'interactive'` too (`routes/channels/whatsapp.ts`, `normalizeMessageType`), with no
 * slot. Only `readInteractive` says whether a row holds a component; never branch on `message_type`.
 *
 * THE SLOT is discriminated on `kind`. Known today:
 *   - `page`: a hub page card (#5333 decision 11), `{ kind: 'page', page, params }`. It opens the page's
 *     CURRENT version, never one pinned at send: `page` is the slug (immutable, so it names the same
 *     page for good) and `params` the values the card opens it with, which the renderer checks against
 *     the page's current `params` schema, dropping what it rejects.
 *   - `template`: a builder's template or a built-in (#4751 Phase 1, `interactive-template.ts`),
 *     `{ kind: 'template', template, version, params, data, status }`. Unlike a page card it is PINNED
 *     (decision 8): `version` names the definition it was sent with, which it renders and against which
 *     the server checks its actions, so a published change never alters a past message. `data` is the
 *     snapshot of its declared data tool's result (its declared `fields` only), capped at
 *     `INTERACTIVE_TEMPLATE_DATA_MAX_CHARS`; `status` is `open` until an action answers it.
 * Reserved, not designed yet: `mcp_app` (Phase 5). No other variant may take that name.
 *
 * READING goes through `readInteractive` only, on every surface (backend, web, mobile). It never
 * throws: a missing slot, a kind this build does not know (reserved or newer) and a malformed payload
 * all read as `fallback`, and the caller renders `message_text`. Older and newer clients coexist
 * because of it, so evolve the slot only additively:
 *   - a new field on a known kind is optional; an older reader strips it (Zod drops unknown keys);
 *   - a change to what a field means, or a new required field, is a new `kind`, which older readers
 *     show as their fallback.
 */

/**
 * The `message_type` a component message is written with. Shared with inbound WhatsApp interactive
 * replies, which carry no component: read the slot with `readInteractive`, never by this type.
 */
export const INTERACTIVE_MESSAGE_TYPE = 'interactive';

/** Kind names held for later variants (#4751 decisions 1, 2): read as unknown until they ship. */
export const INTERACTIVE_RESERVED_KINDS = ['mcp_app'] as const;

/** The most JSON text, in characters, a page card's `params` takes. */
export const INTERACTIVE_PAGE_PARAMS_MAX_CHARS = 4 * 1024;

/** A page card: the page's slug and the values it opens with (none: `{}`). */
export const interactivePageSchema = z.object({
  kind: z.literal('page'),
  page: pageSlugSchema,
  params: z.record(z.unknown()).default({}).superRefine(capped(INTERACTIVE_PAGE_PARAMS_MAX_CHARS, 'params')),
});
export type InteractivePageInput = z.input<typeof interactivePageSchema>;
export type InteractivePage = z.output<typeof interactivePageSchema>;

/**
 * The most JSON text a template message's `params` takes, in UTF-16 code units
 * (`JSON.stringify(params).length`): at most 12 KiB of UTF-8 (3 bytes per unit, worst case).
 */
export const INTERACTIVE_TEMPLATE_PARAMS_MAX_CHARS = 4 * 1024;
/**
 * The most JSON text a template message's `data` snapshot takes, in UTF-16 code units
 * (`JSON.stringify(data).length`, what `withinJsonLength` measures), not bytes: at most 96 KiB of
 * UTF-8 (3 bytes per unit, worst case). Every history read and the conversation's RPC returns carry
 * it, so a list RPC's worst case counts 96 KiB of snapshot plus 12 KiB of params per template
 * message it returns.
 */
export const INTERACTIVE_TEMPLATE_DATA_MAX_CHARS = 32 * 1024;
/** A template message's lifecycle: `open` takes actions; `answered` shows its receipt. */
export const INTERACTIVE_TEMPLATE_STATUSES = ['open', 'answered'] as const;
export type InteractiveTemplateStatus = (typeof INTERACTIVE_TEMPLATE_STATUSES)[number];

/** A refinement capping a value's JSON text at `max` characters (the unmeasurable refused). */
function capped(max: number, field: string) {
  return (value: unknown, ctx: z.RefinementCtx) => {
    if (!withinJsonLength(value, max)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: `${field} is at most ${max} characters of JSON` });
    }
  };
}

/**
 * A template message: the template's name, the version it is pinned to (a positive integer, the hub's
 * stored version of that name; a built-in's own version for a built-in), the params it was sent with,
 * its data snapshot (null when the template declares no data) and its status.
 */
export const interactiveTemplateSchema = z.object({
  kind: z.literal('template'),
  template: interactiveTemplateNameSchema,
  version: z.number().int().positive(),
  params: z.record(z.unknown()).default({}).superRefine(capped(INTERACTIVE_TEMPLATE_PARAMS_MAX_CHARS, 'params')),
  data: z.record(z.unknown()).nullable().default(null).superRefine(capped(INTERACTIVE_TEMPLATE_DATA_MAX_CHARS, 'data')),
  status: z.enum(INTERACTIVE_TEMPLATE_STATUSES).default('open'),
});
export type InteractiveTemplateInput = z.input<typeof interactiveTemplateSchema>;
export type InteractiveTemplate = z.output<typeof interactiveTemplateSchema>;

/** `metadata.interactive`: one of the known variants. A writer parses through this before storing. */
export const interactiveSchema = z.discriminatedUnion('kind', [interactivePageSchema, interactiveTemplateSchema]);
export type InteractiveInput = z.input<typeof interactiveSchema>;
export type Interactive = z.output<typeof interactiveSchema>;
export type InteractiveKind = Interactive['kind'];

/** The kinds this contract defines a payload for: the union's own members, so the two never drift. */
export const INTERACTIVE_KINDS = [...interactiveSchema.optionsMap.keys()] as readonly InteractiveKind[];

/** Why a message reads as its `message_text`. */
export type InteractiveFallbackReason =
  /** No `metadata.interactive` (or no metadata). */
  | 'absent'
  /** A `kind` this build does not know: a reserved one, or one a newer writer added. */
  | 'unknown_kind'
  /** A known kind whose payload does not parse, or a slot that is not an object with a string `kind`. */
  | 'malformed';

export type InteractiveRead =
  | { status: 'known'; interactive: Interactive }
  | { status: 'fallback'; reason: InteractiveFallbackReason };

const KNOWN_KINDS: ReadonlySet<unknown> = new Set(INTERACTIVE_KINDS);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * The one reader of `metadata.interactive`. Takes a message's `metadata` as any surface holds it (an
 * object, the stored JSON string, null or undefined) and gives back the parsed known variant, or
 * `fallback`, on which the caller renders `message_text`. Never throws.
 */
export function readInteractive(metadata: unknown): InteractiveRead {
  try {
    let meta = metadata;
    if (typeof meta === 'string') {
      try {
        meta = JSON.parse(meta);
      } catch {
        return { status: 'fallback', reason: 'malformed' };
      }
    }
    if (meta === null || meta === undefined) return { status: 'fallback', reason: 'absent' };
    if (!isRecord(meta)) return { status: 'fallback', reason: 'malformed' };
    const slot = meta.interactive;
    if (slot === null || slot === undefined) return { status: 'fallback', reason: 'absent' };
    if (!isRecord(slot) || typeof slot.kind !== 'string') return { status: 'fallback', reason: 'malformed' };
    if (!KNOWN_KINDS.has(slot.kind)) return { status: 'fallback', reason: 'unknown_kind' };
    const parsed = interactiveSchema.safeParse(slot);
    return parsed.success
      ? { status: 'known', interactive: parsed.data }
      : { status: 'fallback', reason: 'malformed' };
  } catch {
    return { status: 'fallback', reason: 'malformed' };
  }
}
